import { Box, Typography, Skeleton, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { ServiceCardNew } from './ServiceCardNew';
import type { MarketplaceService } from '../../types/pricing';

interface RelatedServicesProps {
  serviceId: string;
  /** Optional catalog instance slug for scoped navigation */
  catalogInstance?: string;
}

interface RelatedResponse {
  serviceId: string;
  related: (MarketplaceService & { relation: string })[];
  source_category: string;
  source_brand: string;
}

/**
 * Fetch with retry — handles transient API failures (cold starts, restarts).
 * Retries up to `retries` times with exponential backoff starting at `delayMs`.
 */
async function fetchWithRetry(url: string, retries = 2, delayMs = 800): Promise<Response> {
  for (let attempt = 0; attempt <= retries; attempt++) {
    const res = await fetch(url);
    if (res.ok) return res;
    // Don't retry 4xx client errors (except 404 which can be transient during API restarts)
    if (res.status >= 400 && res.status < 500 && res.status !== 404) {
      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
    }
    if (attempt < retries) {
      await new Promise((resolve) => setTimeout(resolve, delayMs * (attempt + 1)));
    } else {
      throw new Error(`HTTP ${res.status}: ${res.statusText} (after ${retries + 1} attempts)`);
    }
  }
  throw new Error('fetchWithRetry: unreachable');
}

/**
 * "Customers Also Viewed" horizontal carousel for service detail pages.
 * Fetches related services by category -> brand -> flagship fallback.
 * Retries on transient failures (API restarts, cold starts).
 */
export default function RelatedServices({ serviceId, catalogInstance }: RelatedServicesProps) {
  const router = useRouter();
  const [data, setData] = useState<RelatedResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!serviceId) return;
    let cancelled = false;
    setLoading(true);
    fetchWithRetry(`/api/catalog/services/${serviceId}/related?limit=8`)
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => console.error('[RelatedServices] fetch error:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [serviceId]);

  const scroll = useCallback((direction: 'left' | 'right') => {
    if (!scrollRef.current) return;
    const amount = 320;
    scrollRef.current.scrollBy({
      left: direction === 'left' ? -amount : amount,
      behavior: 'smooth',
    });
  }, []);

  const handleCardClick = useCallback(
    (service: MarketplaceService) => {
      const basePath = catalogInstance
        ? `/c/${catalogInstance}/services/${service.id}`
        : `/services/${service.id}`;
      router.push(basePath);
    },
    [router, catalogInstance],
  );

  // Don't render if no related services
  if (!loading && (!data?.related || data.related.length === 0)) {
    return null;
  }

  return (
    <Box sx={{ mt: 6, mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>
          Customers Also Viewed
        </Typography>
        {data && data.related.length > 3 && (
          <Box>
            <IconButton onClick={() => scroll('left')} size="small" aria-label="Scroll left">
              <ChevronLeft />
            </IconButton>
            <IconButton onClick={() => scroll('right')} size="small" aria-label="Scroll right">
              <ChevronRight />
            </IconButton>
          </Box>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', gap: 2, overflow: 'hidden' }}>
          {[...Array(4)].map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width={280}
              height={220}
              sx={{ flexShrink: 0, borderRadius: 2 }}
            />
          ))}
        </Box>
      ) : (
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            pb: 1,
            // Hide scrollbar but keep functionality
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.2)',
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
          }}
        >
          {data?.related.map((service) => (
            <Box
              key={service.id}
              sx={{
                flexShrink: 0,
                width: { xs: 260, sm: 280, md: 300 },
                scrollSnapAlign: 'start',
              }}
            >
              <ServiceCardNew service={service} onCardClick={handleCardClick} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
